import React, { useState, useEffect } from 'react';
import { Plus, Edit3, Trash2, Tag, Save, X, Palette, Hash } from 'lucide-react';
import api from '../utils/api';


const TagManagementSettings = ({ user }) => {
  const [tags, setTags] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [newTag, setNewTag] = useState({
    name: '',
    color: '#6B7280',
    description: ''
  });
  const [loading, setLoading] = useState(false);

  const tagColors = [
    '#6B7280', '#EF4444', '#F59E0B', '#10B981', '#3B82F6', 
    '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#84CC16'
  ];

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/settings/tags`);
      setTags(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
      // Fallback to mock data for now if API fails
      setTags([
        { id: '1', name: 'urgent', color: '#EF4444', description: 'High priority items', usage_count: 15 },
        { id: '2', name: 'feature', color: '#3B82F6', description: 'New feature development', usage_count: 28 },
        { id: '3', name: 'bug', color: '#F59E0B', description: 'Bug fixes and issues', usage_count: 12 },
        { id: '4', name: 'client-work', color: '#10B981', description: 'Client deliverables', usage_count: 22 },
        { id: '5', name: 'research', color: '#8B5CF6', description: 'Research and discovery', usage_count: 8 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTag = async () => {
    if (!newTag.name.trim()) return;

    try {
      setLoading(true);
      if (editingTag) {
        // Update existing tag
        const response = await api.put(`/settings/tags/${editingTag.id}`, newTag);
        setTags(prev => prev.map(tag => 
          tag.id === editingTag.id ? response.data : tag
        ));
      } else {
        // Create new tag
        const response = await api.post(`/settings/tags`, newTag);
        setTags(prev => [...prev, response.data]);
      }
      
      handleCloseModal();
    } catch (error) {
      console.error('Error saving tag:', error);
      if (error.response?.status === 400) {
        alert('A tag with this name already exists.');
      } else {
        alert('Error saving tag. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTag = async (tagId) => {
    const tag = tags.find(t => t.id === tagId);
    if (!window.confirm(`Delete "${tag.name}" tag? This will remove it from all tasks using this tag.`)) return;

    try {
      setLoading(true);
      await api.delete(`/settings/tags/${tagId}`);
      setTags(prev => prev.filter(t => t.id !== tagId));
    } catch (error) {
      console.error('Error deleting tag:', error);
      alert('Error deleting tag. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setNewTag({
      name: tag.name,
      color: tag.color,
      description: tag.description || ''
    });
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingTag(null);
    setNewTag({ name: '', color: '#6B7280', description: '' });
  };

  if (loading && tags.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Tag Management</h2>
            <p className="text-gray-600">Create and manage tags for organizing tasks and projects across your workspace.</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Tag
          </button>
        </div>
      </div>

      {/* Tags List */}
      <div className="space-y-3">
        {tags.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tags yet</h3>
            <p className="text-gray-500 mb-4">Create your first tag to start organizing your workspace.</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-purple-700 bg-purple-100 hover:bg-purple-200"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Tag
            </button>
          </div>
        ) : (
          tags.map((tag) => (
            <div key={tag.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span 
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white"
                    style={{ backgroundColor: tag.color }}
                  >
                    <Hash className="w-3 h-3 mr-1" />
                    {tag.name}
                  </span>
                  <div>
                    <p className="text-sm text-gray-900">{tag.description || 'No description'}</p>
                    <p className="text-xs text-gray-500">Used in {tag.usage_count} items</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEditTag(tag)}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteTag(tag.id)}
                    className="p-2 text-red-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Tag Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingTag ? 'Edit Tag' : 'Add New Tag'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tag Name *</label>
                <input
                  type="text"
                  value={newTag.name}
                  onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., urgent, feature, bug"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newTag.description}
                  onChange={(e) => setNewTag({ ...newTag, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="2"
                  placeholder="Optional description for this tag"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                <div className="flex flex-wrap gap-2">
                  {tagColors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setNewTag({ ...newTag, color })}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        newTag.color === color ? 'border-gray-900 scale-110' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <div className="mt-2 flex items-center space-x-2">
                  <span 
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white"
                    style={{ backgroundColor: newTag.color }}
                  >
                    <Hash className="w-3 h-3 mr-1" />
                    {newTag.name || 'preview'}
                  </span>
                  <span className="text-sm text-gray-500">Preview</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTag}
                disabled={!newTag.name.trim()}
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingTag ? 'Update Tag' : 'Create Tag'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagManagementSettings;